import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

// Establishes one Socket.IO connection per logged-in tab/session and keeps
// it open for the lifetime of the session. Any page can subscribe to
// specific events (e.g. 'queue:updated', 'patient:status-changed') via the
// useSocketEvent hook below, so the doctor and receptionist portals - even
// open in different tabs or on different devices - see changes the moment
// they happen, instead of waiting for the next polling interval.
export function SocketProvider({ children }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user) {
      // Logged out - make sure any existing connection is closed
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      return;
    }

    // In production (Docker) the socket connects to the same origin the
    // page was served from. REACT_APP_SOCKET_URL can override this for
    // local development where frontend and backend run on different ports.
    const socketUrl = process.env.REACT_APP_SOCKET_URL || undefined;
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
    });

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
}

// Convenience hook: subscribe to a single socket event for the lifetime of
// the calling component. Automatically re-subscribes if the underlying
// socket instance changes (e.g. after login).
export function useSocketEvent(eventName, handler) {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;
    socket.on(eventName, handler);
    return () => {
      socket.off(eventName, handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, eventName]);
}
